// Handler factory for expense type and record CRUD — 9 handlers covering
// category/subcategory management, workspace expenses, and transaction-level expenses.

import * as workspaceService from '../services/workspaceService';
import { writeErrorMessage } from '../services/firestoreWrites';
import * as transactionService from '../services/transactionService';
import { resolveExpenseSelection, validateExpenseNode, planExpenseNodeMove } from '../utils/expenseTaxonomy';
import { deriveFinancialFieldsForModule } from '../commissions/index';
import { buildPricingSettings } from '../commissions/pricing';
import { normalizeBrokerage } from '../commissions/brokerage';
import { DEFAULT_EXP_MODULE_SETTINGS } from '../config/workspaceDefaults';

const LEGACY_BROKERAGE = normalizeBrokerage({});

const normalizeDateOnlyValue = (value) => {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : '';
};

export function useExpenseHandlers({
    selectedWorkspaceId,
    user,
    expenseTypes,
    transactions,
    showToast,
    expModuleSettings = DEFAULT_EXP_MODULE_SETTINGS,
    // Passed in, not read from useBrokerage(): App.js calls this hook above the BrokerageProvider.
    brokerage = LEGACY_BROKERAGE,
}) {
    // One deal's expense edit reprices that deal under its own plan.
    const priceDeal = (deal) => deriveFinancialFieldsForModule(deal, buildPricingSettings({
        allTransactions: transactions,
        expModuleSettings,
        brokerage,
        currentTxId: deal.id,
        formData: deal,
    }));
    const handleCreateExpenseType = async (nameOrPayload, parentCategoryId = null) => {
        if (!selectedWorkspaceId) return null;
        if (!user) {
            showToast('Sign in before saving.', 'error');
            return null;
        }
        const input = typeof nameOrPayload === 'string'
            ? { name: nameOrPayload, parentCategoryId }
            : (nameOrPayload || {});
        const validation = validateExpenseNode({
            name: input.name,
            parentCategoryId: input.parentCategoryId || null
        }, expenseTypes);
        if (!validation.valid) {
            showToast(validation.message, 'error');
            return null;
        }
        try {
            const timestamp = Date.now();
            const payload = {
                name: validation.normalizedName,
                parentCategoryId: validation.normalizedParentCategoryId,
                createdAt: timestamp,
                updatedAt: timestamp,
                createdBy: user.uid,
                updatedBy: user.uid
            };
            const docRef = await workspaceService.createExpenseType(selectedWorkspaceId, payload);
            showToast(validation.normalizedParentCategoryId ? 'Expense subcategory saved.' : 'Expense category saved.');
            return { id: docRef.id, ...payload };
        } catch (error) {
            console.error('Error creating expense type:', error);
            showToast(`Failed to save expense category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleUpdateExpenseType = async (typeId, updates) => {
        if (!selectedWorkspaceId || !typeId) return;
        if (!user) {
            showToast('Sign in before updating.', 'error');
            return;
        }
        const nextValues = typeof updates === 'string' ? { name: updates } : (updates || {});
        const existingType = expenseTypes.find(type => type.id === typeId);
        const validation = validateExpenseNode({
            id: typeId,
            name: nextValues.name,
            parentCategoryId: nextValues.parentCategoryId ?? existingType?.parentCategoryId ?? null
        }, expenseTypes);
        if (!validation.valid) {
            showToast(validation.message, 'error');
            return;
        }
        try {
            await workspaceService.updateExpenseType(selectedWorkspaceId, typeId, {
                name: validation.normalizedName,
                parentCategoryId: validation.normalizedParentCategoryId,
                updatedAt: Date.now(),
                updatedBy: user.uid
            });
            showToast('Expense category updated.');
        } catch (error) {
            console.error('Error updating expense type:', error);
            showToast(`Failed to update expense category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleMoveExpenseType = async (typeId, targetParentCategoryId = null, options = {}) => {
        if (!selectedWorkspaceId || !typeId) return;
        if (!user) {
            showToast('Sign in before updating.', 'error');
            return;
        }
        const plan = planExpenseNodeMove({
            nodeId: typeId,
            targetParentCategoryId,
            childCategoryMode: options.childCategoryMode || 'reparent'
        }, expenseTypes);
        if (!plan.valid) {
            showToast(plan.message || 'Unable to move expense category.', 'error');
            return;
        }
        try {
            await workspaceService.batchUpdateExpenseTypes(selectedWorkspaceId, plan.updates, user.uid);
            showToast(plan.isPromotion ? 'Expense subcategory promoted.' : plan.isDemotion ? 'Expense category demoted.' : 'Expense category moved.');
        } catch (error) {
            console.error('Error moving expense type:', error);
            showToast(`Failed to move expense category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleDeleteExpenseType = async (typeId) => {
        if (!selectedWorkspaceId || !typeId) return;
        try {
            await workspaceService.deleteExpenseType(selectedWorkspaceId, typeId);
            showToast('Expense category deleted.');
        } catch (error) {
            console.error('Error deleting expense type:', error);
            showToast(`Failed to delete expense category: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleCreateWorkspaceExpense = async (expenseData = {}) => {
        if (!selectedWorkspaceId) return;
        if (!user) {
            showToast('Sign in before adding expenses.', 'error');
            return;
        }
        const normalizedDate = normalizeDateOnlyValue(expenseData.date);
        if (!normalizedDate) {
            showToast('Provide a valid expense date.', 'error');
            return;
        }
        const rawAmount = Number(expenseData.amount);
        if (!Number.isFinite(rawAmount) || rawAmount === 0) {
            showToast('Enter a valid expense amount.', 'error');
            return;
        }
        const normalizedAmount = Math.round(rawAmount * 100) / 100;
        const resolvedSelection = resolveExpenseSelection(expenseData, expenseTypes);
        if (!resolvedSelection.categoryName) {
            showToast('Select or enter an expense category.', 'error');
            return;
        }
        const name = (expenseData.name || '').trim();
        if (!name) {
            showToast('Expense description is required.', 'error');
            return;
        }
        const transactionId = (expenseData.transactionId || '').trim();
        const linkedTransaction = transactionId ? transactions.find(t => t?.id === transactionId) : null;
        const resolvedStatus = (linkedTransaction?.status || '').trim() || null;
        const notes = (expenseData.notes || '').trim();
        const timestamp = Date.now();
        const payload = {
            name,
            amount: normalizedAmount,
            status: resolvedStatus,
            date: normalizedDate,
            categoryId: resolvedSelection.categoryId,
            categoryName: resolvedSelection.categoryName,
            subcategoryId: resolvedSelection.subcategoryId,
            subcategoryName: resolvedSelection.subcategoryName || null,
            typeId: resolvedSelection.typeId,
            typeName: resolvedSelection.typeName || resolvedSelection.categoryName,
            customCategoryName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            customTypeName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            transactionId: transactionId || null,
            notes: notes || null,
            createdAt: timestamp,
            createdBy: user.uid,
            updatedAt: timestamp,
            updatedBy: user.uid
        };
        try {
            await workspaceService.createWorkspaceExpense(selectedWorkspaceId, payload);
            showToast('Expense recorded.');
        } catch (error) {
            console.error('Error recording workspace expense:', error);
            showToast(`Failed to record expense: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleUpdateWorkspaceExpense = async (expenseId, expenseData = {}) => {
        if (!selectedWorkspaceId || !expenseId) return;
        if (!user) {
            showToast('Sign in before editing expenses.', 'error');
            return;
        }
        const normalizedDate = normalizeDateOnlyValue(expenseData.date);
        if (!normalizedDate) {
            showToast('Provide a valid expense date.', 'error');
            return;
        }
        const rawAmount = Number(expenseData.amount);
        if (!Number.isFinite(rawAmount) || rawAmount === 0) {
            showToast('Enter a valid expense amount.', 'error');
            return;
        }
        const normalizedAmount = Math.round(rawAmount * 100) / 100;
        const resolvedSelection = resolveExpenseSelection(expenseData, expenseTypes);
        if (!resolvedSelection.categoryName) {
            showToast('Select or enter an expense category.', 'error');
            return;
        }
        const name = (expenseData.name || '').trim();
        if (!name) {
            showToast('Expense description is required.', 'error');
            return;
        }
        const transactionId = (expenseData.transactionId || '').trim();
        const linkedTransaction = transactionId ? transactions.find(t => t?.id === transactionId) : null;
        const resolvedStatus = (linkedTransaction?.status || '').trim() || null;
        const notes = (expenseData.notes || '').trim();
        const payload = {
            name,
            amount: normalizedAmount,
            status: resolvedStatus,
            date: normalizedDate,
            categoryId: resolvedSelection.categoryId,
            categoryName: resolvedSelection.categoryName,
            subcategoryId: resolvedSelection.subcategoryId,
            subcategoryName: resolvedSelection.subcategoryName || null,
            typeId: resolvedSelection.typeId,
            typeName: resolvedSelection.typeName || resolvedSelection.categoryName,
            customCategoryName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            customTypeName: resolvedSelection.categoryId ? null : resolvedSelection.categoryName,
            transactionId: transactionId || null,
            notes: notes || null,
            updatedAt: Date.now(),
            updatedBy: user.uid
        };
        try {
            await workspaceService.updateWorkspaceExpense(selectedWorkspaceId, expenseId, payload);
            showToast('Expense updated.');
        } catch (error) {
            console.error('Error updating workspace expense:', error);
            showToast(`Failed to update expense: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    const handleUpdateTransactionExpense = async (transactionId, expenseIndex, expenseData = {}) => {
        if (!selectedWorkspaceId || !transactionId || !Number.isInteger(expenseIndex) || expenseIndex < 0) return;
        const existingTransaction = transactions.find(t => t?.id === transactionId);
        if (!existingTransaction) {
            showToast('Transaction not found.', 'error');
            return;
        }
        const normalizedDate = normalizeDateOnlyValue(expenseData.date);
        if (!normalizedDate) {
            showToast('Provide a valid expense date.', 'error');
            return;
        }
        const rawAmount = Number(expenseData.amount);
        if (!Number.isFinite(rawAmount) || rawAmount === 0) {
            showToast('Enter a valid expense amount.', 'error');
            return;
        }
        const resolvedSelection = resolveExpenseSelection(expenseData, expenseTypes);
        if (!resolvedSelection.categoryName) {
            showToast('Select an expense category.', 'error');
            return;
        }
        const name = (expenseData.name || '').trim();
        if (!name) {
            showToast('Expense description is required.', 'error');
            return;
        }
        const existingExpenses = Array.isArray(existingTransaction.expenses) ? [...existingTransaction.expenses] : [];
        if (!existingExpenses[expenseIndex]) {
            showToast('Expense entry not found.', 'error');
            return;
        }
        existingExpenses[expenseIndex] = {
            ...existingExpenses[expenseIndex],
            categoryId: resolvedSelection.categoryId,
            categoryName: resolvedSelection.categoryName,
            subcategoryId: resolvedSelection.subcategoryId,
            subcategoryName: resolvedSelection.subcategoryName || null,
            typeId: resolvedSelection.typeId || resolvedSelection.categoryId,
            typeName: resolvedSelection.typeName || resolvedSelection.categoryName,
            description: name,
            name,
            amount: Math.round(rawAmount * 100) / 100,
            date: normalizedDate,
            notes: (expenseData.notes || '').trim() || null
        };
        const payload = priceDeal({
            ...existingTransaction,
            expenses: existingExpenses
        });
        try {
            await transactionService.updateTransaction(selectedWorkspaceId, transactionId, payload);
            showToast('Transaction expense updated.');
        } catch (error) {
            console.error('Error updating transaction expense:', error);
            showToast(writeErrorMessage(error, 'Failed to update transaction expense.'), 'error');
            throw error;
        }
    };

    const handleDeleteTransactionExpense = async (transactionId, expenseIndex) => {
        if (!selectedWorkspaceId || !transactionId || !Number.isInteger(expenseIndex) || expenseIndex < 0) return;
        const existingTransaction = transactions.find(t => t?.id === transactionId);
        if (!existingTransaction) {
            showToast('Transaction not found.', 'error');
            return;
        }
        const existingExpenses = Array.isArray(existingTransaction.expenses) ? [...existingTransaction.expenses] : [];
        if (!existingExpenses[expenseIndex]) {
            showToast('Expense entry not found.', 'error');
            return;
        }
        existingExpenses.splice(expenseIndex, 1);
        const payload = priceDeal({
            ...existingTransaction,
            expenses: existingExpenses
        });
        try {
            await transactionService.updateTransaction(selectedWorkspaceId, transactionId, payload);
            showToast('Transaction expense removed.');
        } catch (error) {
            console.error('Error deleting transaction expense:', error);
            showToast(writeErrorMessage(error, 'Failed to delete transaction expense.'), 'error');
            throw error;
        }
    };

    const handleDeleteWorkspaceExpense = async (expenseId) => {
        if (!selectedWorkspaceId || !expenseId) return;
        try {
            await workspaceService.deleteWorkspaceExpense(selectedWorkspaceId, expenseId);
            showToast('Expense removed.');
        } catch (error) {
            console.error('Error deleting workspace expense:', error);
            showToast(`Failed to delete expense: ${error?.message || 'Unknown error'}`, 'error');
            throw error;
        }
    };

    return {
        handleCreateExpenseType,
        handleUpdateExpenseType,
        handleMoveExpenseType,
        handleDeleteExpenseType,
        handleCreateWorkspaceExpense,
        handleUpdateWorkspaceExpense,
        handleUpdateTransactionExpense,
        handleDeleteTransactionExpense,
        handleDeleteWorkspaceExpense,
    };
}
